import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MaterialReactTable } from "material-react-table";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import VerifiedIcon from "@mui/icons-material/Verified";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { toast } from "react-toastify";

import { axiosInstance } from "../../../../../api/axios";
import { useProjectApi } from "../../../hooks/useProjectApi";
import { EmptyState } from "../../../components/common/EmptyState";
import { EmployeeSelect } from "../../../components/common/EmployeeSelect";
import { ManualHelpButton } from "../../../components/common/HelperFunc";
import { SummaryMetric } from "../../../components/common/SummaryMetric";
import { MRT_Localization_KO } from "../../../constants/localiztion";
import { FONT_PROPS } from "../../../styles/colors";

const STATUS_META = {
  pending: { label: "검토 대기", color: "warning", bg: "#fffbeb", fg: "#b45309", icon: <PendingActionsIcon /> },
  approved: { label: "승인", color: "success", bg: "#ecfdf5", fg: "#047857", icon: <VerifiedIcon /> },
  rejected: { label: "거절", color: "error", bg: "#fef2f2", fg: "#b91c1c", icon: <ThumbDownAltIcon /> },
};

const URGENCY_META = {
  low: { label: "낮음", color: "success", bg: "#f0fdf4", fg: "#15803d", rank: 1 },
  medium: { label: "주의", color: "warning", bg: "#fffbeb", fg: "#b45309", rank: 2 },
  high: { label: "긴급", color: "error", bg: "#fef2f2", fg: "#b91c1c", rank: 3 },
};

const nullable = (value) => (value === undefined || value === null ? "" : String(value));

const normalizeSearchText = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/\s+/g, "");

const truncateOfficialText = (text = "") =>
  String(text).replace(/\s+/g, " ").trim().slice(0, 100);

const resolvePhotoUrl = (photoUrl) => {
  if (!photoUrl) return "";
  if (/^https?:\/\//i.test(photoUrl)) return photoUrl;
  return `${axiosInstance.defaults.baseURL || ""}${photoUrl}`;
};

const getMachineLabel = (machine) =>
  [
    machine.safe_machine_name || machine.machine_name || machine.machine_manage_code || `설비 ${machine.machine_id}`,
    machine.machine_manage_code,
  ]
    .filter(Boolean)
    .join(" / ");

const getCategoryLabel = (category) =>
  [category.danger_category, category.danger_cause].filter(Boolean).join(" / ") ||
  `분류 ${category.danger_category_id}`;

const getRegisterAssetLabel = (asset) =>
  [
    asset.machine_display_name ||
      asset.safe_machine_name ||
      asset.machine_name ||
      asset.machine_manage_code ||
      `설비 ${asset.machine_id}`,
    asset.process_display_name || asset.process_name || `공정 ${asset.process_id}`,
  ]
    .filter(Boolean)
    .join(" / ");

const getProposalLocationText = (proposal) =>
  proposal?.mapped_location_name || proposal?.location_text || "장소 미입력";

const getProposalMachineText = (proposal) =>
  proposal?.mapped_machine_name || proposal?.machine_text || "설비 미입력";

const getDateText = (value) => {
  if (!value) return "-";
  return String(value).replace("T", " ").slice(0, 16);
};

const toCount = (value) => {
  const count = Number(value);
  return Number.isFinite(count) ? count : 0;
};

const getProposalFollowUp = (proposal = {}) => {
  const linked = toCount(proposal.linked_asset_count);
  const evaluated = toCount(proposal.current_month_eval_count);
  const approvedEval = toCount(proposal.approved_eval_count);
  const improveRequired = toCount(proposal.improve_required_count);
  const improveDone = toCount(proposal.improve_done_count);

  return {
    masterDone: proposal.status === "approved" && Boolean(proposal.approved_danger_cause_id),
    linked,
    evaluated,
    approvedEval,
    improveRequired,
    improveDone,
  };
};

function FollowUpChips({ proposal, compact = false }) {
  if (proposal.status === "pending") {
    return <Chip size="small" color="warning" variant="outlined" label="검토 대기" />;
  }
  if (proposal.status === "rejected") {
    return <Chip size="small" color="error" variant="outlined" label="거절 완료" />;
  }

  const flow = getProposalFollowUp(proposal);
  const steps = [
    {
      key: "master",
      label: "마스터",
      done: flow.masterDone,
      helper: proposal.approved_danger_cause_id ? `#${proposal.approved_danger_cause_id}` : "",
    },
    {
      key: "link",
      label: "평가대상",
      done: flow.linked > 0,
      helper: flow.linked > 0 ? `${flow.linked}건` : "",
    },
    {
      key: "eval",
      label: "현재 주기 평가",
      done: flow.evaluated > 0,
      helper: flow.evaluated > 0 ? `${flow.evaluated}건` : "",
    },
    {
      key: "approve",
      label: "승인",
      done: flow.approvedEval > 0,
      helper: flow.approvedEval > 0 ? `${flow.approvedEval}건` : "",
    },
  ];

  if (flow.improveRequired > 0) {
    steps.push({
      key: "improve",
      label: "개선",
      done: flow.improveDone >= flow.improveRequired,
      helper: `${flow.improveDone}/${flow.improveRequired}`,
    });
  }

  if (compact) {
    const doneCount = steps.filter((step) => step.done).length;
    const improveText =
      flow.improveRequired > 0
        ? ` · 개선 ${flow.improveDone}/${flow.improveRequired}`
        : "";

    return (
      <Stack spacing={0.35} sx={{ minWidth: 0, maxWidth: "100%" }}>
        <Chip
          size="small"
          color={doneCount === steps.length ? "success" : "default"}
          variant={doneCount === steps.length ? "filled" : "outlined"}
          label={`${doneCount}/${steps.length} 완료`}
          sx={{
            width: "fit-content",
            height: 24,
            fontWeight: 900,
            "& .MuiChip-label": { px: 0.9 },
          }}
        />
        <Typography
          variant="caption"
          sx={{
            display: "block",
            color: "#64748b",
            fontWeight: 800,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          연결 {flow.linked} · 평가 {flow.evaluated} · 승인 {flow.approvedEval}
          {improveText}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
      {steps.map((step) => (
        <Chip
          key={step.key}
          size="small"
          color={step.done ? "success" : "default"}
          variant={step.done ? "filled" : "outlined"}
          icon={step.done ? <CheckCircleIcon /> : undefined}
          label={compact || !step.helper ? step.label : `${step.label} ${step.helper}`}
          sx={{
            fontWeight: 800,
            maxWidth: compact ? 112 : "none",
            "& .MuiChip-label": {
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
            "& .MuiChip-icon": { fontSize: 15 },
          }}
        />
      ))}
    </Stack>
  );
}

function FollowUpBoard({ summary, onShowApproved, onConnectApproved, onOpenWizard }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 2.5 },
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: "#fff",
      }}
    >
      <Stack spacing={2}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between">
          <Box>
            <Typography sx={{ fontWeight: 900, fontSize: 18 }}>승인 제안 후속 처리</Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ alignSelf: { xs: "flex-start", md: "center" } }}>
            <Button size="small" variant="outlined" onClick={onShowApproved}>
              승인 제안 보기
            </Button>
            <Button size="small" variant="contained" onClick={onConnectApproved} disabled={summary.notLinked <= 0}>
              평가대상 연결
            </Button>
            <Button size="small" variant="outlined" color="secondary" onClick={() => onOpenWizard()} disabled={summary.linked <= 0}>
              안전점검/후속 조치
            </Button>
          </Stack>
        </Stack>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
            gap: 1.25,
          }}
        >
          <SummaryCard icon={<VerifiedIcon />} label="마스터 등록" value={`${summary.mastered}건`} color="#16a34a" helper="승인 완료" />
          <SummaryCard icon={<AssignmentTurnedInIcon />} label="평가대상 연결" value={`${summary.linked}건`} color="#2563eb" helper={`미연결 ${summary.notLinked}건`} />
          <SummaryCard icon={<FactCheckIcon />} label="현재 주기 평가" value={`${summary.evaluated}건`} color="#7c3aed" helper={`평가 대기 ${summary.notEvaluated}건`} />
          <SummaryCard icon={<ReportProblemIcon />} label="개선 남음" value={`${summary.improveOpen}건`} color="#dc2626" helper="개선 필요 중 미완료" />
        </Box>
      </Stack>
    </Paper>
  );
}

const getInitial = (name) => String(name || "?").trim().slice(0, 1);

const findTextMatch = (items, keyword, labelGetter, idKey) => {
  const normalizedKeyword = normalizeSearchText(keyword);
  if (!normalizedKeyword) return "";
  const match = (items || []).find((item) => {
    const label = normalizeSearchText(labelGetter(item));
    return label.includes(normalizedKeyword) || normalizedKeyword.includes(label);
  });
  return match ? String(match[idKey]) : "";
};

const buildReviewDefaults = (proposal, options) => {
  const locationId =
    nullable(proposal.location_id) ||
    findTextMatch(options.locations, proposal.location_text, (item) => item.location_path || item.location_name, "location_id");
  const machineId =
    nullable(proposal.machine_id) ||
    findTextMatch(options.machines, proposal.machine_text, getMachineLabel, "machine_id");
  const assetCandidates = (options.registerAssets || []).filter((asset) => {
    const matchesMachine = machineId ? String(asset.machine_id) === String(machineId) : true;
    const matchesLocation = locationId
      ? (asset.location_ids || []).includes(Number(locationId))
      : true;
    return matchesMachine && matchesLocation;
  });

  return {
    reviewer_emp_id: "",
    location_id: locationId,
    machine_id: machineId,
    register_asset_id:
      assetCandidates.length === 1 ? String(assetCandidates[0].register_asset_id) : "",
    danger_category_id: nullable(proposal.danger_category_id),
    danger_description: truncateOfficialText(proposal.approved_danger_description || proposal.proposal_text),
    reject_reason: "",
  };
};

const selectMenuProps = {
  PaperProps: {
    sx: {
      maxHeight: 360,
      maxWidth: 560,
    },
  },
};

function StatusChip({ status }) {
  const meta = STATUS_META[status] || STATUS_META.pending;
  return (
    <Chip
      size="small"
      color={meta.color}
      icon={meta.icon}
      label={meta.label}
      sx={{ "& .MuiChip-icon": { fontSize: 16 } }}
    />
  );
}

function UrgencyChip({ urgency }) {
  const meta = URGENCY_META[urgency] || URGENCY_META.medium;
  return (
    <Chip
      size="small"
      color={meta.color}
      label={meta.label}
      sx={{ fontWeight: 800 }}
    />
  );
}

function SummaryCard({ icon, label, value, color = "#2563eb", helper, active = false, onClick }) {
  if (!onClick) return <SummaryMetric label={label} value={value} color={color} helper={helper} />;
  return (
    <Paper
      elevation={0}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-pressed={onClick ? active : undefined}
      onClick={onClick}
      onKeyDown={(event) => {
        if (!onClick || (event.key !== "Enter" && event.key !== " ")) return;
        event.preventDefault();
        onClick();
      }}
      sx={{
        p: { xs: 1.5, md: 2 },
        height: "100%",
        border: "1px solid",
        borderColor: active ? color : "divider",
        borderRadius: 2,
        bgcolor: active ? `${color}0d` : "#fff",
        position: "relative",
        overflow: "hidden",
        cursor: onClick ? "pointer" : "default",
        transition: "150ms ease",
        boxShadow: active ? `inset 0 0 0 1px ${color}33` : "none",
        "&:hover": onClick
          ? {
              borderColor: color,
              bgcolor: `${color}10`,
              transform: "translateY(-1px)",
            }
          : undefined,
      }}
    >
      <Box sx={{ position: "absolute", inset: "0 auto 0 0", width: 4, bgcolor: color }} />
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Avatar sx={{ bgcolor: `${color}18`, color, width: 38, height: 38 }}>{icon}</Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
            {label}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.15 }}>
            {value}
          </Typography>
          {helper ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              {helper}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  );
}

function PriorityReviewPanel({ proposal, pendingCount, onApprove, onReject }) {
  if (!proposal) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 2.5 },
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          bgcolor: "#fff",
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ xs: "flex-start", md: "center" }} justifyContent="space-between">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: "#ecfdf5", color: "#047857" }}>
              <AssignmentTurnedInIcon />
            </Avatar>
            <Box>
            <Typography sx={{ fontWeight: 900 }}>검토 대기 제안이 없습니다</Typography>
            <Typography variant="body2" color="text.secondary">
                근로자가 모바일 제안 화면에서 유해·위험요인을 접수하면 이곳에 표시됩니다.
            </Typography>
            </Box>
          </Stack>
          <Chip size="small" color="success" label="대기 0건" />
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 2.5 },
        border: "1px solid",
        borderColor: "#fecaca",
        borderRadius: 2,
        bgcolor: "#fff",
      }}
    >
      <Stack direction={{ xs: "column", lg: "row" }} spacing={2} justifyContent="space-between">
        <Stack spacing={1} sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip size="small" color="error" icon={<ReportProblemIcon />} label="우선 검토" />
            <UrgencyChip urgency={proposal.urgency} />
            <Chip size="small" label={`대기 ${pendingCount}건`} />
            <Typography variant="caption" color="text.secondary">
              접수 {getDateText(proposal.created_at)}
            </Typography>
          </Stack>
          <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.35 }}>
            {proposal.proposal_text}
          </Typography>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1} color="text.secondary">
            <Stack direction="row" spacing={0.75} alignItems="center">
              <LocationOnIcon sx={{ fontSize: 17 }} />
              <Typography variant="body2">{getProposalLocationText(proposal)}</Typography>
            </Stack>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <PrecisionManufacturingIcon sx={{ fontSize: 17 }} />
              <Typography variant="body2">{getProposalMachineText(proposal)}</Typography>
            </Stack>
          </Stack>
        </Stack>
        <Stack direction={{ xs: "row", lg: "column" }} spacing={1} sx={{ minWidth: { lg: 150 } }}>
          <Button fullWidth variant="contained" color="success" startIcon={<FactCheckIcon />} onClick={() => onApprove(proposal)}>
            승인
          </Button>
          <Button fullWidth variant="outlined" color="error" startIcon={<ThumbDownAltIcon />} onClick={() => onReject(proposal)}>
            거절
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

function ProposalMobileCard({ proposal, onApprove, onReject, onConnect, onOpenWizard, onOpenDetail }) {
  const isPending = proposal.status === "pending";
  const isApproved = proposal.status === "approved";
  const linkedCount = toCount(proposal.linked_asset_count);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: "#fff",
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="center">
            <Avatar sx={{ width: 34, height: 34, bgcolor: "#eff6ff", color: "#1d4ed8", fontWeight: 900 }}>
              {getInitial(proposal.proposer_name || proposal.proposer_emp_id)}
            </Avatar>
            <Box>
              <Typography sx={{ fontWeight: 900 }}>{proposal.proposer_name || proposal.proposer_emp_id}</Typography>
              <Typography variant="caption" color="text.secondary">
                {getDateText(proposal.created_at)}
              </Typography>
            </Box>
          </Stack>
          <StatusChip status={proposal.status} />
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <UrgencyChip urgency={proposal.urgency} />
          <Chip size="small" label={getProposalLocationText(proposal)} />
          <Chip size="small" label={getProposalMachineText(proposal)} />
        </Stack>

        <Typography sx={{ lineHeight: 1.65 }}>{proposal.proposal_text}</Typography>

        {proposal.approved_danger_description ? (
          <Alert severity="success" icon={<VerifiedIcon />} sx={{ py: 0.75 }}>
            {proposal.approved_danger_description}
          </Alert>
        ) : null}
        {proposal.reject_reason ? (
          <Alert severity="error" icon={<ThumbDownAltIcon />} sx={{ py: 0.75 }}>
            {proposal.reject_reason}
          </Alert>
        ) : null}

        <Box sx={{ pt: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75, fontWeight: 800 }}>
            후속 처리
          </Typography>
          <FollowUpChips proposal={proposal} />
        </Box>

        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            color="inherit"
            startIcon={<VisibilityIcon />}
            onClick={() => onOpenDetail?.(proposal)}
          >
            상세
          </Button>
          {proposal.photo_url ? (
            <Button size="small" href={resolvePhotoUrl(proposal.photo_url)} target="_blank" rel="noreferrer" startIcon={<OpenInNewIcon />}>
              사진
            </Button>
          ) : null}
          <Box sx={{ flex: 1 }} />
          {isApproved ? (
            <Button
              size="small"
              variant={linkedCount > 0 ? "outlined" : "contained"}
              color="primary"
              onClick={() => onOpenWizard(proposal)}
            >
              후속 조치 등록
            </Button>
          ) : null}
          {isPending ? (
            <>
              <Button size="small" variant="contained" color="success" startIcon={<FactCheckIcon />} onClick={() => onApprove(proposal)}>
                승인
              </Button>
              <Button size="small" variant="outlined" color="error" onClick={() => onReject(proposal)}>
                거절
              </Button>
            </>
          ) : (
            <Chip
              size="small"
              icon={proposal.status === "approved" ? <CheckCircleIcon /> : <ThumbDownAltIcon />}
              label={proposal.status === "approved" ? "승인 완료" : "거절 완료"}
              color={proposal.status === "approved" ? "success" : "error"}
              variant="outlined"
            />
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}

function ProposalDetailDialog({ open, proposal, onClose }) {
  const photoUrl = resolvePhotoUrl(proposal?.photo_url);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 2.5 },
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle sx={{ px: { xs: 2, sm: 2.5 }, py: 2, borderBottom: "1px solid #e5e7eb" }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                제안 상세
              </Typography>
              {proposal ? <StatusChip status={proposal.status} /> : null}
              {proposal ? <UrgencyChip urgency={proposal.urgency} /> : null}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block", fontWeight: 700 }}>
              접수 {getDateText(proposal?.created_at)}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ p: { xs: 2, sm: 2.5 }, bgcolor: "#f8fafc" }}>
        {proposal ? (
          <Grid container spacing={2}>
            <Grid item xs={12} md={7}>
              <Stack spacing={1.5}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    border: "1px solid #e5e7eb",
                    borderRadius: 2,
                    bgcolor: "#fff",
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 900 }}>
                    제안 내용
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.75,
                      fontWeight: 900,
                      lineHeight: 1.75,
                      whiteSpace: "pre-wrap",
                      wordBreak: "keep-all",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {proposal.proposal_text}
                  </Typography>
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    border: "1px solid #e5e7eb",
                    borderRadius: 2,
                    bgcolor: "#fff",
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 900 }}>
                      첨부 사진
                    </Typography>
                  </Stack>
                  {photoUrl ? (
                    <Box
                      sx={{
                        display: "block",
                        height: { xs: 220, md: 300 },
                        border: "1px solid #e2e8f0",
                        borderRadius: 2,
                        bgcolor: "#f8fafc",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        component="img"
                        src={photoUrl}
                        alt="첨부 사진"
                        sx={{
                          display: "block",
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        height: 120,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "1px dashed #cbd5e1",
                        borderRadius: 2,
                        color: "#94a3b8",
                        fontWeight: 900,
                      }}
                    >
                      첨부 사진 없음
                    </Box>
                  )}
                </Paper>
              </Stack>
            </Grid>

            <Grid item xs={12} md={5}>
              <Stack spacing={1.5}>
                <Paper elevation={0} sx={{ p: 1.75, border: "1px solid #e5e7eb", borderRadius: 2, bgcolor: "#fff" }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 900 }}>
                    접수자
                  </Typography>
                  <Typography sx={{ mt: 0.5, fontWeight: 900 }}>
                    {proposal.proposer_name || proposal.proposer_emp_id || "-"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontWeight: 700 }}>
                    {proposal.proposer_department_name || "부서 미입력"}
                  </Typography>
                </Paper>

                <Paper elevation={0} sx={{ p: 1.75, border: "1px solid #e5e7eb", borderRadius: 2, bgcolor: "#fff" }}>
                  <Stack spacing={1.25}>
                    <Box>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <LocationOnIcon fontSize="small" sx={{ color: "#2563eb" }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 900 }}>
                          장소/라인
                        </Typography>
                      </Stack>
                      <Typography sx={{ mt: 0.45, fontWeight: 900, lineHeight: 1.45 }}>
                        {getProposalLocationText(proposal)}
                      </Typography>
                    </Box>
                    <Divider />
                    <Box>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <PrecisionManufacturingIcon fontSize="small" sx={{ color: "#0f766e" }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 900 }}>
                          설비/작업대상
                        </Typography>
                      </Stack>
                      <Typography sx={{ mt: 0.45, fontWeight: 900, lineHeight: 1.45 }}>
                        {getProposalMachineText(proposal)}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>

                <Paper elevation={0} sx={{ p: 1.75, border: "1px solid #e5e7eb", borderRadius: 2, bgcolor: "#fff" }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 900 }}>
                    후속 처리
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <FollowUpChips proposal={proposal} />
                  </Box>
                </Paper>

                {proposal.approved_danger_description ? (
                  <Alert severity="success" icon={<VerifiedIcon />} sx={{ borderRadius: 2 }}>
                    {proposal.approved_danger_description}
                  </Alert>
                ) : null}
                {proposal.reject_reason ? (
                  <Alert severity="error" icon={<ThumbDownAltIcon />} sx={{ borderRadius: 2 }}>
                    {proposal.reject_reason}
                  </Alert>
                ) : null}
              </Stack>
            </Grid>
          </Grid>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function ReviewDialog({
  open,
  mode,
  proposal,
  form,
  options,
  isSaving,
  isMobile,
  onClose,
  onChange,
  onSubmit,
}) {
  const isApprove = mode === "approve";
  const title = isApprove ? "근로자 제안 승인" : "근로자 제안 거절";
  const photoUrl = resolvePhotoUrl(proposal?.photo_url);
  const selectedLocationId = nullable(form.location_id);
  const selectedMachineId = nullable(form.machine_id);
  const filteredMachines = useMemo(() => {
    const machines = options.machines || [];
    if (!selectedLocationId) return machines;
    return machines.filter((machine) =>
      (machine.location_ids || []).includes(Number(selectedLocationId)) ||
      String(machine.machine_id) === String(selectedMachineId)
    );
  }, [options.machines, selectedLocationId, selectedMachineId]);
  const filteredRegisterAssets = useMemo(() => {
    return (options.registerAssets || []).filter((asset) => {
      const matchesMachine = selectedMachineId
        ? String(asset.machine_id) === String(selectedMachineId)
        : true;
      const matchesLocation = selectedLocationId
        ? (asset.location_ids || []).includes(Number(selectedLocationId))
        : true;
      return matchesMachine && matchesLocation;
    });
  }, [options.registerAssets, selectedLocationId, selectedMachineId]);

  const handleUseOriginal = () => {
    onChange("danger_description", truncateOfficialText(proposal?.proposal_text || ""));
  };

  const handleCopyOriginal = async () => {
    try {
      await navigator.clipboard.writeText(proposal?.proposal_text || "");
      toast.info("근로자 원문을 복사했습니다.");
    } catch (error) {
      toast.warning("브라우저 권한 때문에 복사하지 못했습니다.");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ pb: 1.5 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                {title}
              </Typography>
              {proposal ? <StatusChip status={proposal.status} /> : null}
              {proposal ? <UrgencyChip urgency={proposal.urgency} /> : null}
            </Stack>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "#f6f8fb", p: { xs: 2, md: 3 } }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <Paper variant="outlined" sx={{ p: 2, height: "100%", bgcolor: "#fff", borderRadius: 2 }}>
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Avatar sx={{ bgcolor: "#eff6ff", color: "#1d4ed8", fontWeight: 900 }}>
                    {getInitial(proposal?.proposer_name || proposal?.proposer_emp_id)}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 900 }}>
                      {proposal?.proposer_name || proposal?.proposer_emp_id || "-"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {proposal?.proposer_department_name || "부서 미입력"} · 접수 {getDateText(proposal?.created_at)}
                    </Typography>
                  </Box>
                </Stack>

                <Divider />

                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                    근로자 원문
                  </Typography>
                  <Typography sx={{ mt: 0.5, whiteSpace: "pre-wrap", lineHeight: 1.75, wordBreak: "keep-all" }}>
                    {proposal?.proposal_text || "-"}
                  </Typography>
                </Box>

                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <LocationOnIcon color="action" sx={{ fontSize: 18 }} />
                    <Typography variant="body2">{getProposalLocationText(proposal)}</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PrecisionManufacturingIcon color="action" sx={{ fontSize: 18 }} />
                    <Typography variant="body2">{getProposalMachineText(proposal)}</Typography>
                  </Stack>
                </Stack>

                {photoUrl ? (
                  <Button
                    variant="outlined"
                    href={photoUrl}
                    target="_blank"
                    rel="noreferrer"
                    startIcon={<OpenInNewIcon />}
                    sx={{ justifyContent: "flex-start" }}
                  >
                    첨부 사진 열기
                  </Button>
                ) : (
                  <Alert severity="info" sx={{ py: 0.75 }}>
                    첨부 사진이 없습니다.
                  </Alert>
                )}
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={7}>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: "#fff", borderRadius: 2 }}>
              <Stack spacing={2}>
                <EmployeeSelect
                  employees={options.employees || []}
                  value={form.reviewer_emp_id}
                  onChange={(value) => onChange("reviewer_emp_id", value)}
                  label="검토자"
                  helperText="팀을 먼저 선택하고 이름이나 사번으로 검색할 수 있습니다."
                />

                {isApprove ? (
                  <>
                    <Box>
                      <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography sx={{ fontWeight: 900 }}>마스터 유해·위험요인 문구</Typography>
                        <Stack direction="row" spacing={1}>
                          <Button size="small" onClick={handleCopyOriginal}>
                            원문 복사
                          </Button>
                          <Button size="small" variant="outlined" onClick={handleUseOriginal}>
                            원문 100자 적용
                          </Button>
                        </Stack>
                      </Stack>
                      <TextField
                        fullWidth
                        required
                        multiline
                        minRows={3}
                        placeholder="예: 조립 A라인 프레스 주변 오일 누유로 미끄러짐 위험"
                        value={form.danger_description}
                        onChange={(event) => onChange("danger_description", event.target.value.slice(0, 100))}
                        helperText={`${form.danger_description.length}/100자`}
                      />
                    </Box>

                    <Grid container spacing={1.5}>
                      <Grid item xs={12}>
                        <TextField
                          select
                          fullWidth
                          label="위험분류/원인"
                          value={form.danger_category_id}
                          onChange={(event) => onChange("danger_category_id", event.target.value)}
                          SelectProps={{ MenuProps: selectMenuProps }}
                        >
                          <MenuItem value="">미지정</MenuItem>
                          {(options.categories || []).map((category) => (
                            <MenuItem key={category.danger_category_id} value={String(category.danger_category_id)}>
                              {getCategoryLabel(category)}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          select
                          fullWidth
                          label="참고 장소"
                          value={form.location_id}
                          onChange={(event) => onChange("location_id", event.target.value)}
                          SelectProps={{ MenuProps: selectMenuProps }}
                        >
                          <MenuItem value="">미지정</MenuItem>
                          {(options.locations || []).map((location) => (
                            <MenuItem key={location.location_id} value={String(location.location_id)}>
                              {location.location_path || location.location_name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          select
                          fullWidth
                          label="설비"
                          value={form.machine_id}
                          onChange={(event) => onChange("machine_id", event.target.value)}
                          SelectProps={{ MenuProps: selectMenuProps }}
                        >
                          <MenuItem value="">미지정</MenuItem>
                          {filteredMachines.map((machine) => (
                            <MenuItem key={machine.machine_id} value={String(machine.machine_id)}>
                              {getMachineLabel(machine)}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          select
                          fullWidth
                          required
                          label="평가대상 공정"
                          value={form.register_asset_id}
                          onChange={(event) => onChange("register_asset_id", event.target.value)}
                          SelectProps={{ MenuProps: selectMenuProps }}
                          helperText="승인하면 선택한 공정-설비에 유해·위험요인이 자동 연결됩니다."
                        >
                          <MenuItem value="">선택</MenuItem>
                          {filteredRegisterAssets.map((asset) => (
                            <MenuItem key={asset.register_asset_id} value={String(asset.register_asset_id)}>
                              {getRegisterAssetLabel(asset)}
                            </MenuItem>
                          ))}
                        </TextField>
                        {form.machine_id && filteredRegisterAssets.length === 0 ? (
                          <Alert severity="warning" sx={{ mt: 1 }}>
                            선택한 설비에 연결된 공정이 없습니다. 먼저 기준정보에서 설비-공정 연결을 확인해주세요.
                          </Alert>
                        ) : null}
                      </Grid>
                    </Grid>
                  </>
                ) : (
                  <TextField
                    fullWidth
                    required
                    multiline
                    minRows={5}
                    label="거절 사유"
                    placeholder="예: 이미 등록된 유해·위험요인입니다. 사진상 위치 확인이 어려워 라인명을 보완해 다시 접수해주세요."
                    value={form.reject_reason}
                    onChange={(event) => onChange("reject_reason", event.target.value.slice(0, 300))}
                    helperText={`${form.reject_reason.length}/300자`}
                  />
                )}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button color="inherit" onClick={onClose} disabled={isSaving}>
          취소
        </Button>
        <Button
          variant="contained"
          color={isApprove ? "success" : "error"}
          startIcon={isApprove ? <FactCheckIcon /> : <ThumbDownAltIcon />}
          onClick={onSubmit}
          disabled={isSaving}
        >
          {isSaving ? "처리 중" : isApprove ? "승인하고 평가대상 생성" : "거절 처리"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function WorkerHazardProposalReview() {
  const { group_code, project_code } = useParams();
  const navigate = useNavigate();
  const api = useProjectApi(group_code, project_code);
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [statusFilter, setStatusFilter] = useState("pending");
  const [dialog, setDialog] = useState({ open: false, mode: "approve", proposal: null });
  const [reviewForm, setReviewForm] = useState({
    reviewer_emp_id: "",
    location_id: "",
    machine_id: "",
    register_asset_id: "",
    danger_category_id: "",
    danger_description: "",
    reject_reason: "",
  });

  const optionsQuery = useQuery({
    queryKey: ["worker-hazard-proposal-review-options", group_code, project_code],
    queryFn: async () => {
      const response = await api.get("/worker-hazard-proposals/options");
      return response.data || {};
    },
    enabled: Boolean(group_code && project_code),
    placeholderData: { employees: [], locations: [], machines: [], registerAssets: [], categories: [] },
  });

  const allProposalsQuery = useQuery({
    queryKey: ["worker-hazard-proposals", group_code, project_code, "all-summary"],
    queryFn: async () => {
      const response = await api.get("/worker-hazard-proposals");
      return response.data || [];
    },
    enabled: Boolean(group_code && project_code),
    placeholderData: [],
  });

  const proposalsQuery = useQuery({
    queryKey: ["worker-hazard-proposals", group_code, project_code, statusFilter],
    queryFn: async () => {
      const response = await api.get("/worker-hazard-proposals", {
        params:
          statusFilter === "all"
            ? {}
            : { status: statusFilter === "high_pending" ? "pending" : statusFilter },
      });
      return response.data || [];
    },
    enabled: Boolean(group_code && project_code),
    placeholderData: [],
  });

  const proposals = useMemo(() => proposalsQuery.data || [], [proposalsQuery.data]);
  const allProposals = useMemo(() => allProposalsQuery.data || [], [allProposalsQuery.data]);
  const [detailProposal, setDetailProposal] = useState(null);

  const visibleProposals = useMemo(() => {
    const filtered =
      statusFilter === "high_pending"
        ? proposals.filter((proposal) => proposal.status === "pending" && proposal.urgency === "high")
        : proposals;
    return [...filtered].sort((a, b) => {
      if (a.status === "pending" && b.status === "pending") {
        const urgencyDiff =
          (URGENCY_META[b.urgency]?.rank || 0) - (URGENCY_META[a.urgency]?.rank || 0);
        if (urgencyDiff) return urgencyDiff;
      }
      return String(b.created_at || "").localeCompare(String(a.created_at || ""));
    });
  }, [proposals, statusFilter]);

  const summary = useMemo(() => {
    const total = allProposals.length;
    const pending = allProposals.filter((item) => item.status === "pending").length;
    const approved = allProposals.filter((item) => item.status === "approved").length;
    const rejected = allProposals.filter((item) => item.status === "rejected").length;
    const high = allProposals.filter((item) => item.status === "pending" && item.urgency === "high").length;
    return { all: total, total, pending, approved, rejected, high };
  }, [allProposals]);

  const followUpSummary = useMemo(() => {
    const approved = allProposals.filter((item) => item.status === "approved");
    const mastered = approved.filter((item) => item.approved_danger_cause_id).length;
    const linked = approved.filter((item) => toCount(item.linked_asset_count) > 0).length;
    const evaluated = approved.filter((item) => toCount(item.current_month_eval_count) > 0).length;
    const improveOpen = approved.filter((item) => {
      const required = toCount(item.improve_required_count);
      if (required <= 0) return false;
      return toCount(item.improve_done_count) < required;
    }).length;

    return {
      mastered,
      linked,
      notLinked: Math.max(mastered - linked, 0),
      evaluated,
      notEvaluated: Math.max(linked - evaluated, 0),
      improveOpen,
    };
  }, [allProposals]);

  const priorityProposal = useMemo(() => {
    return [...allProposals]
      .filter((proposal) => proposal.status === "pending")
      .sort((a, b) => {
        const urgencyDiff =
          (URGENCY_META[b.urgency]?.rank || 0) - (URGENCY_META[a.urgency]?.rank || 0);
        if (urgencyDiff) return urgencyDiff;
        return String(b.created_at || "").localeCompare(String(a.created_at || ""));
      })[0];
  }, [allProposals]);

  const approvedNotLinkedProposal = useMemo(
    () =>
      [...allProposals]
        .filter(
          (proposal) =>
            proposal.status === "approved" &&
            proposal.approved_danger_cause_id &&
            toCount(proposal.linked_asset_count) === 0
        )
        .sort((a, b) =>
          String(b.reviewed_at || b.created_at || "").localeCompare(
            String(a.reviewed_at || a.created_at || "")
          )
        )[0],
    [allProposals]
  );

  const openWizard = (proposal) => {
    navigate(`/safe/${group_code}/${project_code}/monitoring/inspection`, {
      state: { followUpProposal: proposal || null },
    });
  };

  const openRegisterCauseForProposal = (proposal) => {
    if (!proposal?.approved_danger_cause_id) {
      toast.info("승인 후 생성된 유해·위험요인 마스터가 있어야 평가대상으로 연결할 수 있습니다.");
      return;
    }

    const params = new URLSearchParams();
    params.set("danger_cause_id", proposal.approved_danger_cause_id);
    if (proposal.proposal_id) params.set("proposal_id", proposal.proposal_id);
    if (proposal.location_id) params.set("location_id", proposal.location_id);
    if (proposal.machine_id) params.set("machine_id", proposal.machine_id);
    if (proposal.mapped_machine_name) params.set("machine_name", proposal.mapped_machine_name);
    if (proposal.mapped_location_name) params.set("location_name", proposal.mapped_location_name);
    if (proposal.danger_category) params.set("danger_category", proposal.danger_category);
    if (proposal.danger_cause) params.set("danger_cause", proposal.danger_cause);
    const keyword = proposal.approved_danger_description || proposal.proposal_text || "";
    if (keyword) params.set("keyword", keyword);

    navigate(`/safe/${group_code}/${project_code}/register/cause?${params.toString()}`, {
      state: {
        selectedSituation: {
          danger_cause_id: proposal.approved_danger_cause_id,
          danger_category: proposal.danger_category || "",
          danger_cause: proposal.danger_cause || "",
          danger_description: keyword,
          worker_proposal_source: 1,
          latest_worker_proposal_id: proposal.proposal_id,
          location_id: proposal.location_id || "",
          machine_id: proposal.machine_id || "",
          location_name: proposal.mapped_location_name || proposal.location_text || "",
          machine_name:
            proposal.mapped_machine_name ||
            proposal.safe_machine_name ||
            proposal.machine_text ||
            "",
        },
      },
    });
  };

  const openFirstUnlinkedApprovedProposal = () => {
    if (!approvedNotLinkedProposal) {
      setStatusFilter("approved");
      toast.info("평가대상 연결이 필요한 승인 제안이 없습니다.");
      return;
    }
    openRegisterCauseForProposal(approvedNotLinkedProposal);
  };

  const invalidateProposals = () => {
    queryClient.invalidateQueries({ queryKey: ["worker-hazard-proposals", group_code, project_code] });
  };

  const reviewMutation = useMutation({
    mutationFn: async () => {
      const proposalId = dialog.proposal?.proposal_id;
      if (dialog.mode === "approve") {
        return api.put(`/worker-hazard-proposals/${proposalId}/approve`, {
          reviewer_emp_id: reviewForm.reviewer_emp_id || null,
          location_id: reviewForm.location_id || null,
          machine_id: reviewForm.machine_id || null,
          register_asset_id: reviewForm.register_asset_id || null,
          danger_category_id: reviewForm.danger_category_id || null,
          danger_description: reviewForm.danger_description,
        });
      }
      return api.put(`/worker-hazard-proposals/${proposalId}/reject`, {
        reviewer_emp_id: reviewForm.reviewer_emp_id || null,
        reject_reason: reviewForm.reject_reason,
      });
    },
    onSuccess: (response) => {
      if (dialog.mode === "approve") {
        toast.success("근로자 제안 승인과 평가대상 연결이 완료되었습니다.");
        closeDialog();
        invalidateProposals();
        return;
      }

      toast.success(response.data?.message || "검토 처리가 완료되었습니다.");
      closeDialog();
      invalidateProposals();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "검토 처리 중 오류가 발생했습니다.");
    },
  });

  const openApproveDialog = (proposal) => {
    setDialog({ open: true, mode: "approve", proposal });
    setReviewForm(buildReviewDefaults(proposal, optionsQuery.data || {}));
  };

  const openRejectDialog = (proposal) => {
    setDialog({ open: true, mode: "reject", proposal });
    setReviewForm({
      reviewer_emp_id: "",
      location_id: "",
      machine_id: "",
      register_asset_id: "",
      danger_category_id: "",
      danger_description: "",
      reject_reason: "",
    });
  };

  const closeDialog = () => {
    setDialog({ open: false, mode: "approve", proposal: null });
    setReviewForm({
      reviewer_emp_id: "",
      location_id: "",
      machine_id: "",
      register_asset_id: "",
      danger_category_id: "",
      danger_description: "",
      reject_reason: "",
    });
  };

  const handleReviewFormChange = (key, value) => {
    setReviewForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "location_id" || key === "machine_id") {
        const locationId = key === "location_id" ? value : next.location_id;
        const machineId = key === "machine_id" ? value : next.machine_id;
        const assetCandidates = (optionsQuery.data?.registerAssets || []).filter((asset) => {
          const matchesMachine = machineId ? String(asset.machine_id) === String(machineId) : true;
          const matchesLocation = locationId
            ? (asset.location_ids || []).includes(Number(locationId))
            : true;
          return matchesMachine && matchesLocation;
        });
        next.register_asset_id =
          assetCandidates.length === 1 ? String(assetCandidates[0].register_asset_id) : "";
      }
      if (key === "register_asset_id" && value) {
        const asset = (optionsQuery.data?.registerAssets || []).find(
          (item) => String(item.register_asset_id) === String(value)
        );
        if (asset?.machine_id) next.machine_id = String(asset.machine_id);
      }
      return next;
    });
  };

  const handleReviewSubmit = () => {
    if (dialog.mode === "approve" && !reviewForm.danger_description.trim()) {
      toast.warning("마스터 유해·위험요인 문구를 입력해주세요.");
      return;
    }
    if (dialog.mode === "approve" && !reviewForm.register_asset_id) {
      toast.warning("유해·위험요인을 연결할 평가대상 공정을 선택해주세요.");
      return;
    }
    if (dialog.mode === "reject" && !reviewForm.reject_reason.trim()) {
      toast.warning("거절 사유를 입력해주세요.");
      return;
    }
    reviewMutation.mutate();
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "status",
        header: "상태",
        size: 120,
        Cell: ({ cell }) => <StatusChip status={cell.getValue()} />,
      },
      {
        accessorKey: "urgency",
        header: "긴급도",
        size: 100,
        Cell: ({ cell }) => <UrgencyChip urgency={cell.getValue()} />,
      },
      {
        accessorKey: "proposal_text",
        header: "접수 내용",
        size: 360,
        Cell: ({ row }) => (
          <Box sx={{ maxWidth: 420, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 900,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                wordBreak: "keep-all",
              }}
            >
              {row.original.proposal_text}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "block",
                mt: 0.5,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {getProposalLocationText(row.original)}
            </Typography>
          </Box>
        ),
      },
      {
        accessorKey: "proposer_name",
        header: "제안자",
        size: 170,
        Cell: ({ row }) => (
          <Stack direction="row" spacing={1} alignItems="center">
            <Avatar sx={{ width: 30, height: 30, bgcolor: "#eff6ff", color: "#1d4ed8", fontSize: 14, fontWeight: 900 }}>
              {getInitial(row.original.proposer_name || row.original.proposer_emp_id)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                {row.original.proposer_name || row.original.proposer_emp_id}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {row.original.proposer_department_name || "-"}
              </Typography>
            </Box>
          </Stack>
        ),
      },
      {
        accessorKey: "created_at",
        header: "접수일",
        size: 150,
        Cell: ({ cell }) => getDateText(cell.getValue()),
      },
      {
        accessorKey: "approved_danger_description",
        header: "검토 결과",
        size: 250,
        Cell: ({ row }) => {
          if (row.original.status === "approved") {
            return (
              <Typography variant="body2" sx={{ color: "#047857", fontWeight: 800 }}>
                {row.original.approved_danger_description || `마스터 ID ${row.original.approved_danger_cause_id}`}
              </Typography>
            );
          }
          if (row.original.status === "rejected") {
            return (
              <Typography variant="body2" sx={{ color: "#b91c1c", fontWeight: 800 }}>
                {row.original.reject_reason || "거절 처리"}
              </Typography>
            );
          }
          return <Typography variant="body2" color="text.secondary">검토 대기</Typography>;
        },
      },
      {
        accessorKey: "linked_asset_count",
        header: "후속 처리",
        size: 120,
        maxSize: 140,
        Cell: ({ row }) => <FollowUpChips proposal={row.original} compact />,
      },
      {
        accessorKey: "photo_url",
        header: "사진",
        size: 90,
        Cell: ({ cell }) => {
          const photoUrl = cell.getValue();
          if (!photoUrl) return <Typography color="text.secondary">-</Typography>;
          return (
            <Button
              size="small"
              href={resolvePhotoUrl(photoUrl)}
              target="_blank"
              rel="noreferrer"
              startIcon={<OpenInNewIcon />}
              onClick={(event) => event.stopPropagation()}
            >
              보기
            </Button>
          );
        },
      },
    ],
    []
  );

  const isLoading = proposalsQuery.isLoading || allProposalsQuery.isLoading;

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, height: "100%", overflow: "auto", bgcolor: "#f6f8fb" }}>
      <Stack spacing={2.5}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            bgcolor: "#fff",
          }}
        >
          <Stack spacing={2.5}>
            <Stack direction={{ xs: "column", lg: "row" }} spacing={2} justifyContent="space-between">
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <ReportProblemIcon color="primary" />
                  <Typography variant="h5" sx={{ fontWeight: 900, letterSpacing: 0 }}>
                    근로자 유해·위험요인 제안 검토/승인
                  </Typography>
                  <ManualHelpButton title="근로자 유해·위험요인 제안 검토/승인" />
                </Stack>
              </Box>
            </Stack>

            <Typography variant="body2" color="text.secondary">
              현재 화면 {visibleProposals.length}건 · 전체 {summary.total}건
            </Typography>
          </Stack>
        </Paper>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", lg: "repeat(5, minmax(0, 1fr))" },
            gap: 1.5,
          }}
        >
          <SummaryCard
            icon={<FactCheckIcon />}
            label="전체 접수"
            value={`${summary.total}건`}
            active={statusFilter === "all"}
            onClick={() => setStatusFilter("all")}
          />
          <SummaryCard
            icon={<PendingActionsIcon />}
            label="검토 대기"
            value={`${summary.pending}건`}
            color="#f59e0b"
            active={statusFilter === "pending"}
            onClick={() => setStatusFilter("pending")}
          />
          <SummaryCard
            icon={<VerifiedIcon />}
            label="승인"
            value={`${summary.approved}건`}
            color="#16a34a"
            active={statusFilter === "approved"}
            onClick={() => setStatusFilter("approved")}
          />
          <SummaryCard
            icon={<ThumbDownAltIcon />}
            label="거절"
            value={`${summary.rejected}건`}
            color="#dc2626"
            active={statusFilter === "rejected"}
            onClick={() => setStatusFilter("rejected")}
          />
          <SummaryCard
            icon={<ReportProblemIcon />}
            label="긴급 대기"
            value={`${summary.high}건`}
            color="#ea580c"
            active={statusFilter === "high_pending"}
            onClick={() => setStatusFilter("high_pending")}
          />
        </Box>

        <FollowUpBoard
          summary={followUpSummary}
          onShowApproved={() => setStatusFilter("approved")}
          onConnectApproved={openFirstUnlinkedApprovedProposal}
          onOpenWizard={openWizard}
        />

        <PriorityReviewPanel
          proposal={priorityProposal}
          pendingCount={summary.pending}
          onApprove={openApproveDialog}
          onReject={openRejectDialog}
        />

        {isMobile ? (
          <Stack spacing={1.5}>
            {visibleProposals.length ? (
              visibleProposals.map((proposal) => (
                <ProposalMobileCard
                  key={proposal.proposal_id}
                  proposal={proposal}
                  onApprove={openApproveDialog}
                  onReject={openRejectDialog}
                  onConnect={openRegisterCauseForProposal}
                  onOpenWizard={openWizard}
                  onOpenDetail={setDetailProposal}
                />
              ))
            ) : (
              <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, bgcolor: "#fff" }}>
                <EmptyState
                  compact
                  icon={<AssignmentTurnedInIcon />}
                  title="조건에 맞는 제안이 없습니다"
                />
              </Paper>
            )}
          </Stack>
        ) : (
          <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden", bgcolor: "#fff" }}>
            <MaterialReactTable
              columns={columns}
              data={visibleProposals}
              localization={MRT_Localization_KO}
              enableColumnActions={false}
              enableDensityToggle={false}
              enableFullScreenToggle={false}
              enableColumnFilters={false}
              enableStickyHeader
              initialState={{
                pagination: { pageSize: 10, pageIndex: 0 },
              }}
              state={{
                isLoading,
                showProgressBars: proposalsQuery.isFetching || allProposalsQuery.isFetching,
                showAlertBanner: proposalsQuery.isError || allProposalsQuery.isError,
              }}
              muiToolbarAlertBannerProps={
                proposalsQuery.isError || allProposalsQuery.isError
                  ? {
                      color: "error",
                      children: "근로자 제안 목록을 불러오는 중 오류가 발생했습니다.",
                    }
                  : undefined
              }
              muiTablePaperProps={{
                sx: {
                  boxShadow: "none",
                  width: "100%",
                  maxWidth: "100%",
                  overflow: "hidden",
                  "& *": {
                    fontFamily: `${FONT_PROPS.PRIMARY}, sans-serif !important`,
                  },
                },
              }}
              muiTableContainerProps={{
                sx: {
                  maxHeight: "calc(100vh - 470px)",
                  maxWidth: "100%",
                  overflowX: "auto",
                },
              }}
              muiTableHeadCellProps={{
                sx: {
                  fontWeight: 900,
                  bgcolor: "#f1f5f9",
                  color: "#0f172a",
                  textAlign: "center",
                  "& .Mui-TableHeadCell-Content": {
                    justifyContent: "center",
                  },
                  "& .Mui-TableHeadCell-Content-Wrapper": {
                    textAlign: "center",
                  },
                },
              }}
              muiTableBodyRowProps={({ row }) => ({
                onClick: () => setDetailProposal(row.original),
                sx: {
                  bgcolor: row.original.urgency === "high" && row.original.status === "pending" ? "#fff7ed" : "#fff",
                  cursor: "pointer",
                  "&:hover": {
                    bgcolor: row.original.urgency === "high" && row.original.status === "pending" ? "#ffedd5" : "#f8fafc",
                  },
                },
              })}
              enableRowActions
              positionActionsColumn="last"
              renderRowActions={({ row }) => {
                const isPending = row.original.status === "pending";
                const isApproved = row.original.status === "approved";
                const linkedCount = toCount(row.original.linked_asset_count);
                return (
                  <Stack direction="row" spacing={0.5}>
                    {isApproved ? (
                      <Tooltip title="후속 조치 등록">
                        <IconButton
                          color="primary"
                          onClick={(event) => {
                            event.stopPropagation();
                            openWizard(row.original);
                          }}
                        >
                          {linkedCount > 0 ? <OpenInNewIcon /> : <AssignmentTurnedInIcon />}
                        </IconButton>
                      </Tooltip>
                    ) : null}
                    {isPending ? (
                      <>
                        <Tooltip title="승인">
                          <IconButton
                            color="success"
                            onClick={(event) => {
                              event.stopPropagation();
                              openApproveDialog(row.original);
                            }}
                          >
                            <FactCheckIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="거절 처리">
                          <IconButton
                            color="error"
                            onClick={(event) => {
                              event.stopPropagation();
                              openRejectDialog(row.original);
                            }}
                          >
                            <ThumbDownAltIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <Chip
                        onClick={(event) => event.stopPropagation()}
                        size="small"
                        icon={row.original.status === "approved" ? <CheckCircleIcon /> : <ThumbDownAltIcon />}
                        label={row.original.status === "approved" ? "승인 완료" : "거절 완료"}
                        color={row.original.status === "approved" ? "success" : "error"}
                        variant="outlined"
                      />
                    )}
                  </Stack>
                );
              }}
              renderEmptyRowsFallback={() => (
                <EmptyState
                  compact
                  icon={<AssignmentTurnedInIcon />}
                  title="조건에 맞는 제안이 없습니다"
                  description="근로자가 모바일 제안 화면에서 유해·위험요인을 접수하면 이곳에 표시됩니다."
                />
              )}
            />
          </Paper>
        )}

      </Stack>

      <ReviewDialog
        open={dialog.open}
        mode={dialog.mode}
        proposal={dialog.proposal}
        form={reviewForm}
        options={optionsQuery.data || {}}
        isSaving={reviewMutation.isPending}
        isMobile={isMobile}
        onClose={closeDialog}
        onChange={handleReviewFormChange}
        onSubmit={handleReviewSubmit}
      />
      <ProposalDetailDialog
        open={Boolean(detailProposal)}
        proposal={detailProposal}
        onClose={() => setDetailProposal(null)}
      />
    </Box>
  );
}
